import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { IotIntelligenceCardService } from './iot-intelligence-card.service';
import { IotAnalytics, IotParameter, PowerAnalytics } from './iot-intelligence-card';
import { LIVE_DATA_PARAMETER_RESPONSE } from '../../../private/live-data/live-data/live-data';
import { Line100By200Theme } from '../../../constant/skeleton-theme';

@Component({
    selector: 'app-iot-intelligence-card',
    templateUrl: './iot-intelligence-card.component.html',
    styleUrl: './iot-intelligence-card.component.scss',
    providers: [IotIntelligenceCardService],
})
export class IotIntelligenceCardComponent implements OnInit, OnChanges {
    @Input() deviceId!: string;
    @Input() deviceName!: string;
    @Input() deviceStatus: boolean = false;
    @Input() lastReadTime: string = '';

    activeTab: 'overview' | 'power' | 'maintenance' = 'overview';
    activeRange: 'live' | '6h' | '24h' | '7d' = 'live';
    isLoading = true;
    isError = false;
    parameters: IotParameter[] = [];
    analytics!: IotAnalytics;
    fetchedAt: Date | null = null;
    intelligenceCardTheme = Line100By200Theme;

    readonly Math = Math;

    readonly rangeLabels: Record<string, string> = {
        live: 'Live view',
        '6h': 'Last 6 hours',
        '24h': 'Last 24 hours',
        '7d': 'Last 7 days',
    };

    constructor(private service: IotIntelligenceCardService) {}

    ngOnInit(): void {
        this.loadData();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['deviceId'] && changes['deviceId'].currentValue && !changes['deviceId'].firstChange) {
            this.loadData();
        }
    }

    get rangeLabel(): string {
        return this.rangeLabels[this.activeRange];
    }

    get healthRingOffset(): number {
        if (!this.analytics) return 163.4;
        return 163.4 * (1 - this.analytics.healthScore / 100);
    }

    get healthRingColor(): string {
        if (!this.analytics) return '#8d8d8d';
        const s = this.analytics.healthScore;
        if (s >= 80) return '#007e34';
        if (s >= 50) return '#FF8C27';
        return '#F44336';
    }

    get pfQualityClass(): string {
        switch (this.analytics?.power?.pfQuality) {
            case 'Excellent': return 'kpi-green';
            case 'Good': return 'kpi-blue';
            case 'Fair': return 'kpi-amber';
            case 'Poor': return 'kpi-red';
            default: return 'kpi-gray';
        }
    }

    get maintenanceScoreClass(): string {
        const s = this.analytics?.maintenanceScore ?? 100;
        if (s >= 80) return 'score-good';
        if (s >= 50) return 'score-fair';
        return 'score-poor';
    }

    get maintenanceVerdict(): string {
        const s = this.analytics?.maintenanceScore ?? 100;
        if (s >= 90) return 'Excellent — all parameters nominal';
        if (s >= 80) return 'Good — minor deviations detected';
        if (s >= 60) return 'Fair — attention recommended';
        if (s >= 40) return 'Poor — maintenance required';
        return 'Critical — immediate attention needed';
    }

    get trendRisingNames(): string {
        return this.analytics?.trendRising.map(p => p.parameterName).join(', ') || '';
    }
    get trendFallingNames(): string {
        return this.analytics?.trendFalling.map(p => p.parameterName).join(', ') || '';
    }
    get trendStableNames(): string {
        return this.analytics?.trendStable.map(p => p.parameterName).join(', ') || '';
    }

    setRange(range: 'live' | '6h' | '24h' | '7d'): void {
        this.activeRange = range;
        this.loadData(true);
    }

    loadData(keepTab: boolean = false): void {
        if (!this.deviceId) {
            this.isLoading = false;
            this.isError = true;
            return;
        }
        this.isLoading = true;
        this.isError = false;
        if (!keepTab) {
            this.activeTab = 'overview';
        }
        this.service
            .getDeviceParameters(this.deviceId, this.activeRange)
            .then(response => {
                if (response.hasError) {
                    this.isError = true;
                    return;
                }
                // API may return data in result, view, or data; null → treat as []
                const raw: LIVE_DATA_PARAMETER_RESPONSE[] =
                    (response.result ?? (response as any).view ?? (response as any).data) || [];
                this.parameters = this.processParameters(raw);
                this.analytics = this.computeAnalytics(this.parameters);
                this.fetchedAt = new Date();
            })
            .catch(() => {
                this.isError = true;
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    getParamBarWidth(param: IotParameter): number {
        if (param.maxNum <= param.minNum) return 50;
        return Math.min(100, Math.max(0, ((param.currentNum - param.minNum) / (param.maxNum - param.minNum)) * 100));
    }

    getAvgMarkerPosition(param: IotParameter): number {
        if (param.maxNum <= param.minNum) return 50;
        return Math.min(98, Math.max(2, ((param.avgNum - param.minNum) / (param.maxNum - param.minNum)) * 100));
    }

    getPowerBarWidth(key: 'kw' | 'kvar'): number {
        if (!this.analytics?.power) return 0;
        const max = Math.max(this.analytics.power.kw ?? 0, this.analytics.power.kvar ?? 0, 0.001);
        const val = key === 'kw' ? (this.analytics.power.kw ?? 0) : (this.analytics.power.kvar ?? 0);
        return Math.min(100, Math.max(0, (val / max) * 100));
    }

    getEnergyBarWidth(key: 'active' | 'reactive' | 'apparent'): number {
        if (!this.analytics?.power) return 0;
        const max = Math.max(
            this.analytics.power.activeEnergy ?? 0,
            this.analytics.power.reactiveEnergy ?? 0,
            this.analytics.power.apparentEnergy ?? 0,
            0.001
        );
        const map = {
            active: this.analytics.power.activeEnergy ?? 0,
            reactive: this.analytics.power.reactiveEnergy ?? 0,
            apparent: this.analytics.power.apparentEnergy ?? 0,
        };
        return Math.min(100, Math.max(0, (map[key] / max) * 100));
    }

    private processParameters(raw: LIVE_DATA_PARAMETER_RESPONSE[]): IotParameter[] {
        return raw
            .filter(p => p.DEVICE_ID === this.deviceId || raw.length > 0)
            .map(p => {
                const toFinite = (v: unknown) => { const n = parseFloat(v as string); return isFinite(n) ? n : 0; };
                const current = toFinite(p.lastValue);
                const avgVal = toFinite(p.avg);
                const minVal = toFinite(p.min);
                const maxVal = toFinite(p.max);
                const rawDiff = avgVal !== 0 ? ((current - avgVal) / Math.abs(avgVal)) * 100 : 0;
                const diffPct = isFinite(rawDiff) ? rawDiff : 0;
                const trend: 'up' | 'down' | 'stable' = diffPct > 10 ? 'up' : diffPct < -10 ? 'down' : 'stable';
                const hasRange = minVal !== 0 || maxVal !== 0;
                const inRange = hasRange ? current >= minVal && current <= maxVal : true;
                const healthScore = inRange ? 100 : Math.max(0, 100 - Math.min(Math.abs(diffPct), 100));
                return { ...p, trend, inRange, healthScore, diffPct, currentNum: current, avgNum: avgVal, minNum: minVal, maxNum: maxVal };
            });
    }

    private computeAnalytics(params: IotParameter[]): IotAnalytics {
        const healthScore =
            params.length > 0 ? Math.round(params.reduce((s, p) => s + p.healthScore, 0) / params.length) : 0;
        const alertCount = params.filter(p => !p.inRange).length;

        // substring match OR exact match — prevents 'REACTIVE POWER' matching kW slot
        const findParam = (substrKeys: string[], exactKeys: string[] = []) =>
            params.find(p => {
                const n = p.parameterName.toUpperCase();
                return substrKeys.some(k => n.includes(k.toUpperCase()))
                    || exactKeys.some(k => n === k.toUpperCase());
            });

        const kwP   = findParam(['TOTAL KW', 'TOTAL ACTIVE POWER', 'ACTIVE POWER', 'KW'], ['POWER']);
        const kvarP = findParam(['TOTAL KVAR', 'REACTIVE POWER', 'KVAR']);
        const pfP   = findParam(['AVERAGE POWER FACTOR', 'AVG PF', 'POWER FACTOR AVG', 'AVG POWER FACTOR', 'POWER FACTOR']);
        const pf1P  = findParam(['POWER FACTOR PHASE 1', 'PF PHASE 1', 'PF1', 'PHASE 1 PF']);
        const pf2P  = findParam(['POWER FACTOR PHASE 2', 'PF PHASE 2', 'PF2', 'PHASE 2 PF']);
        const pf3P  = findParam(['POWER FACTOR PHASE 3', 'PF PHASE 3', 'PF3', 'PHASE 3 PF']);
        const aeP   = findParam(['ACTIVE ENERGY', 'KWH', 'ENERGY KWH', 'ACTIVE ENERGY KWH']);
        const reP   = findParam(['REACTIVE ENERGY', 'KVARH', 'REACTIVE ENERGY KVARH']);
        const apP   = findParam(['APPARENT ENERGY', 'KVAH', 'APPARENT ENERGY KVAH']);
        const voltP = findParam(['VOLTAGE', 'VOLT']);
        const currP = findParam(['CURRENT', 'AMPERE', 'AMPS']);
        const freqP = findParam(['FREQUENCY', 'FREQ', 'HZ']);
        const loadP = findParam(['LOAD FACTOR', 'LOAD %'], ['LOAD']);

        const kw = kwP?.currentNum ?? null;
        const kwMax = kwP?.maxNum ?? null;
        const kvar = kvarP?.currentNum ?? null;
        const pfAvg = pfP?.currentNum ?? null;
        const pf1 = pf1P?.currentNum ?? null;
        const pf2 = pf2P?.currentNum ?? null;
        const pf3 = pf3P?.currentNum ?? null;
        const activeEnergy = aeP?.currentNum ?? null;
        const reactiveEnergy = reP?.currentNum ?? null;
        const apparentEnergy = apP?.currentNum ?? null;
        const voltage = voltP?.currentNum ?? null;
        const current = currP?.currentNum ?? null;
        const frequency = freqP?.currentNum ?? null;
        const load = loadP?.currentNum ?? null;

        const loadFactor = kw !== null && kwMax !== null && kwMax > 0
            ? (kw / kwMax) * 100
            : (load !== null ? load : null);
        const energyEfficiency =
            activeEnergy !== null && apparentEnergy !== null && apparentEnergy > 0
                ? (activeEnergy / apparentEnergy) * 100
                : null;
        const reactiveRatio =
            reactiveEnergy !== null && activeEnergy !== null && activeEnergy + reactiveEnergy > 0
                ? (reactiveEnergy / (activeEnergy + reactiveEnergy)) * 100
                : null;

        let pfQuality: PowerAnalytics['pfQuality'] = null;
        if (pfAvg !== null) {
            const pct = pfAvg <= 1 ? pfAvg * 100 : pfAvg;
            pfQuality = pct >= 90 ? 'Excellent' : pct >= 80 ? 'Good' : pct >= 70 ? 'Fair' : 'Poor';
        }

        let phaseBalanceScore: number | null = null;
        const pfs = [pf1, pf2, pf3].filter((v): v is number => v !== null);
        if (pfs.length >= 2) {
            const mean = pfs.reduce((s, v) => s + v, 0) / pfs.length;
            const stddev = Math.sqrt(pfs.reduce((s, v) => s + Math.pow(v - mean, 2), 0) / pfs.length);
            phaseBalanceScore = Math.max(0, Math.round(100 - (stddev / (mean || 1)) * 100 * 3));
        }

        const hasPowerData = kw !== null || kvar !== null || pfAvg !== null
            || voltage !== null || current !== null || frequency !== null || load !== null;

        let availability = 0;
        if (this.deviceStatus) {
            if (this.lastReadTime) {
                const diffMin = (Date.now() - new Date(this.lastReadTime).getTime()) / 60000;
                availability = diffMin < 10 ? 100 : diffMin < 30 ? 95 : 80;
            } else {
                availability = 80;
            }
        }

        const maintenanceScore =
            params.length > 0 ? Math.round((params.filter(p => p.inRange).length / params.length) * 100) : 100;

        return {
            healthScore,
            availability,
            alertCount,
            paramCount: params.length,
            power: { kw, kwMax, kvar, pfAvg, pfQuality, pf1, pf2, pf3, activeEnergy, reactiveEnergy, apparentEnergy, loadFactor, energyEfficiency, reactiveRatio, phaseBalanceScore, voltage, current, frequency, load, hasPowerData },
            maintenanceScore,
            trendRising: params.filter(p => p.trend === 'up'),
            trendFalling: params.filter(p => p.trend === 'down'),
            trendStable: params.filter(p => p.trend === 'stable'),
            maintenanceAlerts: params.filter(p => !p.inRange),
        };
    }
}
